'use client';

import { useState } from 'react';
import { X, UserPlus, ChevronDown } from 'lucide-react';
import { api } from '@/shared/lib/api';
import { ProjectRole, type Project, type User } from '@/shared/lib/types';
import { useNamespacedTranslate } from '@/shared/lib/locales';

const ASSIGNABLE_ROLES = [ProjectRole.EDIT, ProjectRole.VIEW];

interface PendingMember {
  user_id: string;
  name: string;
  email: string;
  role: string;
  isNew?: boolean;
}

interface ShareProjectModalProps {
  project: Project;
  onClose: () => void;
  onSaved?: () => void;
}

export function ShareProjectModal({ project, onClose, onSaved }: ShareProjectModalProps) {
  const t = useNamespacedTranslate('shareProjectModal');
  const roleLabels: Record<string, string> = {
    [ProjectRole.ADMIN]: t('roleAdmin'),
    [ProjectRole.EDIT]: t('roleEdit'),
    [ProjectRole.VIEW]: t('roleView'),
  };

  const initialMembers: PendingMember[] = project.members.map((m: User) => ({
    user_id: m.user_id,
    name: m.name,
    email: m.email,
    role: (m.role_id as string) ?? ProjectRole.VIEW,
  }));

  const [members, setMembers] = useState<PendingMember[]>(initialMembers);
  const [removedIds, setRemovedIds] = useState<Set<string>>(new Set());
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<string>(ProjectRole.VIEW);
  const [roleDropdownOpen, setRoleDropdownOpen] = useState(false);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleInvite = async () => {
    const email = inviteEmail.trim();
    if (!email) return;
    if (members.some((m) => m.email === email && !removedIds.has(m.user_id))) {
      setError(t('alreadyCollaboratorError'));
      return;
    }
    setLookupLoading(true);
    setError(null);
    try {
      const result = await api.getUsers({ email });
      if (!result.items.length) {
        setError(t('userNotFoundError'));
        return;
      }
      const user = result.items[0];
      setMembers((prev) => [
        ...prev,
        { user_id: user.user_id, name: user.name, email: user.email, role: inviteRole, isNew: true },
      ]);
      setRemovedIds((prev) => { const s = new Set(prev); s.delete(user.user_id); return s; });
      setInviteEmail('');
    } catch {
      setError(t('searchUserError'));
    } finally {
      setLookupLoading(false);
    }
  };

  const handleRoleChange = (userId: string, role: string) => {
    setMembers((prev) => prev.map((m) => m.user_id === userId ? { ...m, role } : m));
  };

  const handleRemove = (userId: string) => {
    setRemovedIds((prev) => new Set(prev).add(userId));
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    try {
      // Remove members marked for removal (only existing ones)
      for (const userId of Array.from(removedIds)) {
        const wasOriginal = initialMembers.some((m) => m.user_id === userId);
        if (wasOriginal) {
          await api.removeUserFromProject(project.project_id, userId);
        }
      }
      // Add new members
      for (const m of members) {
        if (m.isNew && !removedIds.has(m.user_id)) {
          await api.addUserToProject(project.project_id, m.user_id, m.role);
        }
      }
      // Update roles for existing members (not removed, not new)
      for (const m of members) {
        if (!m.isNew && !removedIds.has(m.user_id)) {
          const original = initialMembers.find((o) => o.user_id === m.user_id);
          if (original && original.role !== m.role) {
            await api.updateUserInProject(project.project_id, m.user_id, m.role);
          }
        }
      }
      onSaved?.();
      onClose();
    } catch {
      setError(t('saveChangesError'));
    } finally {
      setIsSaving(false);
    }
  };

  const visibleMembers = members.filter((m) => !removedIds.has(m.user_id));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 p-6 space-y-5">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{t('title')}</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {t('inviteDescription', { projectName: project.name })}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Invite by email */}
        <div>
          <p className="text-sm font-medium text-gray-700 mb-2">{t('inviteByEmailLabel')}</p>
          <div className="flex gap-2">
            <input
              type="email"
              placeholder={t('inviteEmailPlaceholder')}
              value={inviteEmail}
              onChange={(e) => { setInviteEmail(e.target.value); setError(null); }}
              onKeyDown={(e) => e.key === 'Enter' && handleInvite()}
              className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900"
            />
            {/* Role dropdown */}
            <div className="relative">
              <button
                onClick={() => setRoleDropdownOpen((o) => !o)}
                className="flex items-center gap-1 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 bg-white hover:bg-gray-50 min-w-[72px]"
              >
                {roleLabels[inviteRole]}
                <ChevronDown className="h-3.5 w-3.5 text-gray-400" />
              </button>
              {roleDropdownOpen && (
                <div className="absolute right-0 z-10 mt-1 w-28 bg-white border border-gray-200 rounded-lg shadow-md">
                  {ASSIGNABLE_ROLES.map((r) => (
                    <button
                      key={r}
                      className="block w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                      onClick={() => { setInviteRole(r); setRoleDropdownOpen(false); }}
                    >
                      {roleLabels[r]}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {/* Add button */}
            <button
              onClick={handleInvite}
              disabled={lookupLoading || !inviteEmail.trim()}
              className="flex items-center justify-center w-10 h-10 rounded-lg bg-gray-900 text-white hover:bg-gray-700 disabled:opacity-40 transition-colors"
            >
              <UserPlus className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Error */}
        {error && <p className="text-sm text-red-500">{error}</p>}

        {/* Current collaborators */}
        {visibleMembers.length > 0 && (
          <div>
            <p className="text-sm font-medium text-gray-700 mb-2">{t('currentCollaboratorsLabel')}</p>
            <div className="border border-gray-200 rounded-xl divide-y divide-gray-100 overflow-hidden">
              {visibleMembers.map((m) => (
                <div key={m.user_id} className="flex items-center gap-3 px-4 py-3">
                  <span className="flex-1 text-sm text-gray-700 truncate">{m.email}</span>
                  <MemberRoleDropdown
                    role={m.role}
                    disabled={m.role === ProjectRole.ADMIN}
                    translateRole={t}
                    onChange={(role) => handleRoleChange(m.user_id, role)}
                  />
                  {m.role !== ProjectRole.ADMIN && (
                    <button
                      onClick={() => handleRemove(m.user_id)}
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-1">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 transition-colors"
          >
            {t('cancelButton')}
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="px-4 py-2 text-sm rounded-lg bg-gray-900 text-white hover:bg-gray-700 disabled:opacity-50 transition-colors"
          >
            {isSaving ? t('savingButton') : t('saveChangesButton')}
          </button>
        </div>
      </div>
    </div>
  );
}

function MemberRoleDropdown({
  role,
  disabled,
  onChange,
  translateRole,
}: {
  role: string;
  disabled: boolean;
  onChange: (role: string) => void;
  translateRole: (key: string, options?: Record<string, any>) => string;
}) {
  const [open, setOpen] = useState(false);
  const roleLabels: Record<string, string> = {
    [ProjectRole.ADMIN]: translateRole('roleAdmin'),
    [ProjectRole.EDIT]: translateRole('roleEdit'),
    [ProjectRole.VIEW]: translateRole('roleView'),
  };

  if (disabled) {
    return <span className="text-xs text-gray-500">{roleLabels[role] ?? role}</span>;
  }

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-800 transition-colors"
      >
        {roleLabels[role] ?? role}
        <ChevronDown className="h-3 w-3" />
      </button>
      {open && (
        <div className="absolute right-0 z-10 mt-1 w-24 bg-white border border-gray-200 rounded-lg shadow-md">
          {ASSIGNABLE_ROLES.map((r) => (
            <button
              key={r}
              className="block w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-gray-50"
              onClick={() => { onChange(r); setOpen(false); }}
            >
              {roleLabels[r]}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
